import { useDisclosure } from "@mantine/hooks";
import { QueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";

import { FeatureToggle, useIsFeatureEnabled } from "#app/featureToggle";
import { STALE_TIME } from "#app/http/ReactQueryProvider";
import { OptimizationDetailProvider } from "#modules/optimizations/view/OptimizationDetailContext";
import { OptimizationsPanel } from "#modules/optimizations/view/OptimizationsPanel";
import { useAreOptimizationsAvailable } from "#modules/optimizations/view/useAreOptimizationsAvailable";
import { usePartFeaturesBulk } from "#modules/part/view/usePartFeaturesBulk";
import { useAllQuoteRequestLinesBulk } from "#modules/quoteRequestLines/view/useAllQuoteRequestLinesBulk";
import { useCustomInputsBulk } from "#modules/quoteRequestLines/view/useCustomInputsBulk";
import { useProcessStepCatalog } from "#modules/quoteRequestLines/view/useProcessStepCatalog";
import { QuoteRequestLineContainer } from "#modules/quoteRequestLines/views/QuoteRequestLine/QuoteRequestLineContainer";
import { QuoteRequestLineSkeleton } from "#modules/quoteRequestLines/views/QuoteRequestLine/QuoteRequestLineSkeleton";
import {
  AssembliesContextType,
  AssembliesProvider,
} from "#modules/quoteRequests/context/AssembliesContext";
import { quoteRequestQuery } from "#modules/quoteRequests/infra/quoteRequest.query";
import { MultiActionMenu } from "#modules/quoteRequests/view/MultiActionMenu";
import { QuoteRequestFooter } from "#modules/quoteRequests/view/QuoteRequestFooter";
import { QuoteRequestLines } from "#modules/quoteRequests/view/QuoteRequestLines";
import { useQuoteRequest } from "#modules/quoteRequests/view/useQuoteRequest";
import { useQuoteRequestLinesSorting } from "#modules/quoteRequests/view/useQuoteRequestLinesSorting";
import { useQuoteRequestProcessSteps } from "#modules/quoteRequests/view/useQuoteRequestProcessSteps";
import { Icon } from "#shared/icons/Icon";
import { ROUTES } from "#shared/routes";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { quoteRequestAtom, quoteRequestisEditing } from "#shared/view/components/AppShell/Atoms";
import { Box } from "#shared/view/components/Box";
import { Group } from "#shared/view/components/Group";
import { ImportNewPartDropZone } from "#shared/view/components/ImportNewPartDropZone";
import { Stack } from "#shared/view/components/Stack";
import { Title } from "#shared/view/components/Title";
import { Button } from "#shared/view/designSystem/Button";
import { Features } from "#types/FeatureToggleTypes";
import { QuoteRequestLineDto } from "shared";

export const loader =
  (queryClient: QueryClient, quoteRequestId: string | undefined) => async () => {
    const quoteRequest = await queryClient.fetchQuery({
      ...quoteRequestQuery(quoteRequestId),
      staleTime: STALE_TIME,
    });

    return { quoteRequest };
  };

export const QuoteRequestPage = () => {
  const setQuoteRequest = useSetAtom(quoteRequestAtom);
  const setIsEditingQuoteRequest = useSetAtom(quoteRequestisEditing);
  const analytics = useAnalytics();
  const hasTrackedOpenRef = useRef(false);
  const [selectedQuoteRequestLines, setSelectedQuoteRequestLines] = useState<QuoteRequestLineDto[]>(
    [],
  );
  const [activeAssembliesByBatchMap, setActiveAssembliesByBatchMap] = useState<
    Record<string, string>
  >({});
  const [initialQuoteRequestLineIds, setInitialQuoteRequestLineIds] = useState<string[]>([]);
  const [
    isOptimizationsPanelOpened,
    { open: openOptimizationsPanel, close: closeOptimizationsPanel },
  ] = useDisclosure(false);
  const [optimizationIdToOpen, setOptimizationIdToOpen] = useState<string | null>(null);
  const isCostingOptimizationEnabled = useIsFeatureEnabled(Features.COSTING_OPTIMIZATION);
  const areOptimizationsAvailable = useAreOptimizationsAvailable();

  const { quoteRequestId } = useParams();

  const { quoteRequest: initialQuoteRequest } = useLoaderData() as Awaited<
    ReturnType<ReturnType<typeof loader>>
  >;

  const {
    quoteRequest,
    isLoading: isLoadingQuoteRequest,
    isInitialLoading,
  } = useQuoteRequest({
    quoteRequestId,
    options: { initialData: initialQuoteRequest },
  });

  const { quoteRequestProcessSteps, isLoading: isLoadingProcessSteps } =
    useQuoteRequestProcessSteps(quoteRequestId);

  const isLoading = isLoadingQuoteRequest || isLoadingProcessSteps;

  const quoteRequestLineIds = useMemo(
    () => quoteRequest?.quoteRequestLines.map((line) => line.id) || [],
    [quoteRequest],
  );

  useEffect(() => {
    if (initialQuoteRequestLineIds.length === 0) {
      setInitialQuoteRequestLineIds(quoteRequestLineIds);
    }
  }, [initialQuoteRequestLineIds, setInitialQuoteRequestLineIds, quoteRequestLineIds]);

  const { quoteRequestLines, isInitialLoading: isLoadingQuoteRequestLines } =
    useAllQuoteRequestLinesBulk(initialQuoteRequestLineIds, quoteRequestLineIds);

  useProcessStepCatalog();

  const partIds = useMemo(
    () => Array.from(new Set(quoteRequestLines.map((line) => line.part.id))),
    [quoteRequestLines],
  );

  const initialPartIds = useMemo(
    () => Array.from(new Set(initialQuoteRequest.quoteRequestLines.map((line) => line.partId))),
    [initialQuoteRequest],
  );

  const { isInitialLoading: isInitialPartFeaturesLoading } = usePartFeaturesBulk(
    initialPartIds,
    partIds,
  );

  const { isInitialLoading: isInitialCustomInputsLoading } = useCustomInputsBulk(
    {
      quoteRequestId,
    },
    { enabled: !!quoteRequestId },
  );

  const { batches, assemblies, handleSort, sortProperty } =
    useQuoteRequestLinesSorting(quoteRequestLines);
  const memoizedHandleSort = useCallback(handleSort, [handleSort]);

  useEffect(() => {
    if (quoteRequest && !hasTrackedOpenRef.current) {
      hasTrackedOpenRef.current = true;
      analytics.capture(AnalyticsEvent.QuoteRequestOpened, {
        quote_request_id: quoteRequest.id,
        line_count: quoteRequest.quoteRequestLines.length,
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteRequest?.id]);

  useEffect(() => {
    if (quoteRequest) {
      setIsEditingQuoteRequest(true);
      setQuoteRequest({
        ...quoteRequest,
        quoteRequestLines,
      });
    }
  }, [setIsEditingQuoteRequest, setQuoteRequest, quoteRequestLines, quoteRequest]);

  const hasBatches = useMemo(() => {
    return batches.values().some((batch) => batch.length > 1);
  }, [batches]);

  const setActiveAssemblyForBatch = useCallback((partId: string, assemblyId: string) => {
    setActiveAssembliesByBatchMap((prev) => ({
      ...prev,
      [partId]: assemblyId,
    }));
  }, []);

  const resetActiveAssembliesForBatch = useCallback((partId: string) => {
    setActiveAssembliesByBatchMap((prev) => {
      const updated = { ...prev };
      delete updated[partId];
      return updated;
    });
  }, []);

  const stableAssembliesContext = useMemo<AssembliesContextType>(
    () => ({
      assemblies,
      isFetching: isLoading,
      selectedQuoteRequestLines,
      setSelectedQuoteRequestLines,
      activeAssembliesByBatch: activeAssembliesByBatchMap,
      setActiveAssemblyForBatch,
      resetActiveAssembliesForBatch,
    }),
    [
      assemblies,
      isLoading,
      selectedQuoteRequestLines,
      setSelectedQuoteRequestLines,
      activeAssembliesByBatchMap,
      setActiveAssemblyForBatch,
      resetActiveAssembliesForBatch,
    ],
  );

  if (isInitialLoading || isLoadingQuoteRequestLines || isInitialPartFeaturesLoading) {
    return (
      <Stack h="calc(100% - var(--header-height))" gap="xs" justify="space-between" p={"xs"}>
        <Stack gap="xs" flex="1">
          <QuoteRequestLineContainer isDetailsPanelDisplayable={false}>
            <QuoteRequestLineSkeleton />
          </QuoteRequestLineContainer>
        </Stack>
      </Stack>
    );
  }

  if (!quoteRequest) {
    return null;
  }

  if (assemblies.length === 0) {
    return <EmptyQuoteRequestLines />;
  }

  const openOptimizationDetail = (optimizationId: string) => {
    setOptimizationIdToOpen(optimizationId);
    openOptimizationsPanel();
  };

  const closeOptimizationsPanelAndDetail = () => {
    setOptimizationIdToOpen(null);
    closeOptimizationsPanel();
  };

  return (
    <OptimizationDetailProvider value={openOptimizationDetail}>
      <AssembliesProvider value={stableAssembliesContext}>
        <Stack h="calc(100% - var(--header-height))" gap="xs" justify="space-between">
          <Box p={"xs"}>
            <Group align="flex-start" wrap="nowrap" gap="xs">
              <Box flex={1} miw={0}>
                <MultiActionMenu
                  quoteRequestId={quoteRequest.id}
                  orderQuoteRequestLines={memoizedHandleSort}
                  hasBatches={hasBatches}
                />
              </Box>
              {areOptimizationsAvailable ? (
                <FeatureToggle feature={Features.COSTING_OPTIMIZATION}>
                  <Button onClick={openOptimizationsPanel}>Calculer les optimisations</Button>
                </FeatureToggle>
              ) : null}
            </Group>
            {isCostingOptimizationEnabled && areOptimizationsAvailable ? (
              <OptimizationsPanel
                quoteRequest={quoteRequest}
                quoteRequestLines={quoteRequestLines}
                quoteRequestProcessSteps={quoteRequestProcessSteps}
                quoteRequestId={quoteRequest.id}
                opened={isOptimizationsPanelOpened}
                onClose={closeOptimizationsPanelAndDetail}
                optimizationIdToOpen={optimizationIdToOpen}
              />
            ) : null}
            <Stack gap="xs" flex="1" pt={"xs"}>
              <QuoteRequestLines
                batches={batches}
                sortProperty={sortProperty}
                isCustomInputsBulkLoading={isInitialCustomInputsLoading}
              />
              <ImportNewPartDropZone quoteRequestId={quoteRequest.id} />
            </Stack>
          </Box>

          <QuoteRequestFooter
            quoteRequest={quoteRequest}
            isLoading={isLoading}
            quoteRequestLines={quoteRequestLines}
            quoteRequestProcessSteps={quoteRequestProcessSteps}
          />
        </Stack>
      </AssembliesProvider>
    </OptimizationDetailProvider>
  );
};

const EmptyQuoteRequestLines = () => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(ROUTES.partner);
  };

  return (
    <Stack h="100%" justify="center" align="center">
      <Title order={3}>{t("qr.noQrl")}</Title>
      <Button variant="transparent" leftSection={<Icon name="arrowLeft" />} onClick={handleClick}>
        {t("common.backToHome")}
      </Button>
    </Stack>
  );
};
